import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Check, ArrowLeft } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

interface Todo {
  id: string;
  title: string;
  completed: boolean;
  created_at: string;
}

export default function TodoDemo() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [newTodo, setNewTodo] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTodos();
    
    // Real-time subscription
    const channel = supabase
      .channel('todos_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'demo_todos' }, () => {
        fetchTodos();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchTodos = async () => {
    try {
      const { data, error } = await supabase
        .from('demo_todos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        // If table doesn't exist, create it
        if (error.code === '42P01') {
          await createTable();
          return;
        }
        throw error;
      }

      setTodos(data || []);
    } catch (error) {
      console.error('Error fetching todos:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTable = async () => {
    // Table will be created via SQL if doesn't exist
    setTodos([]);
    setLoading(false);
  };

  const addTodo = async () => {
    if (!newTodo.trim()) return;

    try {
      const { error } = await supabase.from('demo_todos').insert([
        { title: newTodo, completed: false },
      ]);

      if (error) throw error;

      toast.success('Todo ditambahkan!');
      setNewTodo('');
      fetchTodos();
    } catch (error) {
      toast.error('Gagal menambahkan todo');
      console.error(error);
    }
  };

  const toggleTodo = async (id: string, completed: boolean) => {
    try {
      const { error } = await supabase
        .from('demo_todos')
        .update({ completed: !completed })
        .eq('id', id);

      if (error) throw error;
      fetchTodos();
    } catch (error) {
      console.error('Error updating todo:', error);
    }
  };

  const deleteTodo = async (id: string) => {
    try {
      const { error } = await supabase.from('demo_todos').delete().eq('id', id);

      if (error) throw error;
      
      toast.success('Todo dihapus!');
      fetchTodos();
    } catch (error) {
      toast.error('Gagal menghapus todo');
    }
  };

  const filteredTodos = todos.filter((todo) => {
    if (filter === 'active') return !todo.completed;
    if (filter === 'completed') return todo.completed;
    return true;
  });

  return (
    <div className="min-h-screen bg-dark p-4">
      <Toaster position="top-right" />
      
      <div className="container mx-auto max-w-2xl">
        {/* Header */}
        <div className="glass-card p-4 mb-6 flex items-center gap-4">
          <a href="/#projects" className="text-neon-blue hover:text-neon-green transition">
            <ArrowLeft size={24} />
          </a>
          <div>
            <h1 className="text-2xl font-bold text-white">Todo App CRUD</h1>
            <p className="text-sm text-gray-400">Demo Aplikasi Todo dengan Supabase Real-time</p>
          </div>
        </div>

        {/* Add Todo */}
        <div className="glass-card p-6 mb-6">
          <div className="flex gap-3">
            <input
              type="text"
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTodo()}
              placeholder="Tambah todo baru..."
              className="flex-1 px-4 py-3 bg-dark-lighter border border-neon-blue/30 rounded-xl text-white focus:outline-none focus:border-neon-blue transition"
            />
            <button onClick={addTodo} className="btn-neon flex items-center gap-2">
              <Plus size={20} />
              Tambah
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-3 mb-6">
          {(['all', 'active', 'completed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                filter === f
                  ? 'bg-neon-blue text-white'
                  : 'glass-card text-gray-400 hover:text-white'
              }`}
            >
              {f === 'all' ? 'Semua' : f === 'active' ? 'Aktif' : 'Selesai'}
            </button>
          ))}
        </div>

        {/* Todo List */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Memuat todos...</div>
        ) : (
          <AnimatePresence>
            {filteredTodos.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <p className="text-gray-400">Tidak ada todo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTodos.map((todo) => (
                  <motion.div
                    key={todo.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    whileHover={{ x: 5 }}
                    className="glass-card p-4 flex items-center gap-4"
                  >
                    <button
                      onClick={() => toggleTodo(todo.id, todo.completed)}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition ${
                        todo.completed
                          ? 'bg-neon-green border-neon-green'
                          : 'border-neon-blue'
                      }`}
                    >
                      {todo.completed && <Check size={16} className="text-white" />}
                    </button>
                    <span
                      className={`flex-1 ${
                        todo.completed
                          ? 'text-gray-500 line-through'
                          : 'text-white'
                      }`}
                    >
                      {todo.title}
                    </span>
                    <button
                      onClick={() => deleteTodo(todo.id)}
                      className="text-red-500 hover:text-red-400 transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>
        )}

        {/* Stats */}
        {todos.length > 0 && (
          <div className="glass-card p-4 mt-6">
            <div className="flex justify-around text-center">
              <div>
                <p className="text-2xl font-bold text-neon-blue">{todos.length}</p>
                <p className="text-sm text-gray-400">Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-neon-green">
                  {todos.filter((t) => t.completed).length}
                </p>
                <p className="text-sm text-gray-400">Selesai</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-500">
                  {todos.filter((t) => !t.completed).length}
                </p>
                <p className="text-sm text-gray-400">Aktif</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
